import {
  PartyPopper, Smartphone, Wallet, Network, DollarSign, ArrowLeftRight,
  Coins, Link2, Cpu, Scale, Repeat, Flag,
} from "lucide-react";

export const STAGES = [
  { id: "A", name: "Wallet Setup", accent: "#4060FF" },
  { id: "B", name: "Fund It", accent: "#00863A" },
  { id: "C", name: "Connect", accent: "#7C3AED" },
  { id: "D", name: "Earn", accent: "#F59E0B" },
];

// 12 lessons · 4 stages. Screenshot files (optional) live at
// Optional per-lesson `video {src, poster, note}` renders a player in the lesson card.
export const LESSONS = [
  {
    stage: "A", icon: PartyPopper, title: "Welcome — how this works", duration: "2 min",
    goal: "Understand the whole journey before you touch anything.",
    steps: [
      "You'll set up a crypto wallet (TP Wallet) that only YOU control — we never hold your funds.",
      "You'll fund it with USDT. The amount is entirely up to you.",
      "You'll connect to CaryPact through a personal invitation and commit funds to hashrate.",
      "CA rewards accrue daily. You decide: keep mining, stake, or cash out.",
    ],
    note: "Honesty first: rewards vary with CA price and are never guaranteed. Train now, decide later.",
  },
  {
    stage: "A", icon: Smartphone, title: "Install TP Wallet", duration: "3 min",
    goal: "Get TokenPocket — the wallet everything runs through.",
    video: {
      src: "/academy-videos/step-1-setup-tp-wallet.mp4",
      poster: "/academy-videos/step-1-poster.jpg",
      note: "This video covers installing TokenPocket AND creating your wallet — it applies to this lesson and the next one.",
    },
    steps: [
      "Open the App Store / Google Play and search “TokenPocket”.",
      "Install the app (blue diamond logo) and open it.",
    ],
    mobile: "App Store / Google Play → TokenPocket.",
    web: "On desktop, add the TokenPocket extension from tokenpocket.pro instead.",
    link: { href: "https://www.tokenpocket.pro/en/download/app", label: "Download TP Wallet" },
  },
  {
    stage: "A", icon: Wallet, title: "Create your wallet & protect the 12 words", duration: "5 min",
    goal: "Your seed phrase IS your wallet. Keep it offline, forever.",
    video: {
      src: "/academy-videos/step-1-setup-tp-wallet.mp4",
      poster: "/academy-videos/step-1-poster.jpg",
      note: "Same video as the previous lesson — the wallet creation and recovery-phrase backup starts around 0:40.",
    },
    steps: [
      "In TP Wallet: Wallet → Create Wallet.",
      "Write the 12 words on paper, in order. Store the paper somewhere safe.",
      "Never screenshot, type, or share the words — no legitimate person will ever ask for them.",
      "Confirm the words in the app to finish.",
    ],
  },
  {
    stage: "A", icon: Network, title: "Add the BOT Chain network", duration: "3 min",
    goal: "Make your CaryPact assets visible inside TP Wallet.",
    steps: [
      "In TP Wallet, open the network selector.",
      "Add “BOT Chain” as a custom network.",
      "Switch to BOT Chain — this is where everything happens.",
    ],
  },
  {
    stage: "B", icon: DollarSign, title: "Get a little BNB for gas", duration: "4 min",
    goal: "Every transaction costs a tiny fee, paid in BNB.",
    steps: [
      "$5–10 of BNB is plenty to start.",
      "Buy it inside TP Wallet, or send BNB from your exchange (Coinbase, Binance…).",
      "Check it arrived: your BNB balance shows in the wallet.",
    ],
  },
  {
    stage: "B", icon: ArrowLeftRight, title: "Bring in USDT & bridge to BOT Chain", duration: "6 min",
    goal: "Move the money you plan to commit into position.",
    steps: [
      "Send your USDT to your TP Wallet on BNB Chain.",
      "Use the bridge to move it from BNB Chain → BOT Chain.",
      "First time? Test with a small amount before sending the rest.",
    ],
  },
  {
    stage: "B", icon: Coins, title: "Add USDT & CA as custom tokens", duration: "2 min",
    goal: "So your balances display correctly on BOT Chain.",
    video: {
      src: "/academy-videos/step-2-add-tokens.mp4",
      poster: "/academy-videos/step-2-poster.jpg",
    },
    steps: [
      "On the BOT Chain network, add USDT as a custom token.",
      "Add CA as a custom token the same way.",
      "Both balances now show in your wallet.",
    ],
  },
  {
    stage: "C", icon: Link2, title: "Join CaryPact with your invitation", duration: "4 min",
    goal: "Your personal invite connects you to the right team.",
    steps: [
      "Tap your personal invitation button below — your invite code fills in automatically.",
      "On mobile, open the link inside TP Wallet's DApp browser (Discover tab).",
      "Register and sign in by signing with your wallet — no password needed.",
    ],
    mobile: "TP Wallet → Discover tab → paste / open your invite link.",
    web: "Open the link in the browser where the TP extension is installed.",
    invite: true,
  },
  {
    stage: "C", icon: Cpu, title: "Buy hashrate — start mining", duration: "3 min",
    goal: "This is the moment your daily CA rewards begin.",
    steps: [
      "In CaryPact, choose the amount of USDT to commit.",
      "Confirm the purchase of computing power (hashrate).",
      "Done — CA mining rewards now accrue daily.",
    ],
  },
  {
    stage: "D", icon: Scale, title: "Mining vs Staking — pick your strategy", duration: "3 min",
    goal: "Know your two ways to earn before choosing.",
    steps: [
      "Mining: your committed USDT generates CA every day.",
      "Staking: stake claimed CA for extra yield — flexible (withdraw anytime) or fixed-term (higher rate).",
      "Most people start with mining, then stake part of their rewards.",
    ],
  },
  {
    stage: "D", icon: Repeat, title: "Claim rewards & cash out", duration: "4 min",
    goal: "Getting your money back out — the part that matters most.",
    steps: [
      "Claim your accrued CA from the CaryPact dashboard.",
      "To cash out: swap CA → BOT → USDT.",
      "Bridge USDT back to BNB Chain and off-ramp via your exchange.",
    ],
  },
  {
    stage: "D", icon: Flag, title: "Final check — request your access", duration: "1 min",
    goal: "Confirm everything is in place, then graduate.",
    steps: [
      "Wallet created — 12 words safely offline.",
      "Funded — BNB for gas + USDT on BOT Chain.",
      "Connected — CaryPact registered, hashrate active.",
      "You know how to claim, stake, and cash out.",
    ],
    note: "All checked? Mark this done and request your BotChainCP member access — we'll set up your login so you can track every dollar.",
    final: true,
  },
];

export const stageOf = (lesson) => STAGES.find((s) => s.id === lesson.stage);
